import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import PageBlock from '@sharedCR/common/PageBlock';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { WithLoadingPropTypes } from 'hocs/withLoading';

import usePopover from 'hooks/usePopover';
import { getConditionalArray } from 'utils/data';

import { selectIsMobile, selectIsTablet } from 'store/global/selectors';
import {
  selectIsAdditionalLoanPurposeFromApplication,
  selectIsHelocPurposePurchaseFromPropertyAndLoanSummary,
  selectIsPurchaseLoanPurposeFromApplication,
  selectSecondaryFinancingAdditionalMortgagesData,
  selectSecondaryFinancingLiabilitiesData,
} from 'store/loanApplications/selectors';

import ActionMenu from '../ActionMenu';
import {
  ENTITY_TYPE_HREF_MAP,
  LOAN_AND_LOCK_SECTION_HREF_MAP,
} from '../EditModal/EditModal.constants';

import {
  additionalMortgagesConfig,
  additionalMortgagesNotFoundTableData,
  liabilitiesConfig,
  liabilitiesNotFoundTableData,
} from './SecondaryFinancing.config';
import { COLUMN_WIDTH } from './SecondaryFinancing.constants';
import { tableStyles } from './SecondaryFinancing.styles';

const SecondaryFinancing = ({ isLoading }) => {
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const secondaryFinancingLiabilitiesData = useSelector(selectSecondaryFinancingLiabilitiesData);
  const secondaryFinancingAdditionalMortgagesData = useSelector(
    selectSecondaryFinancingAdditionalMortgagesData,
  );
  const isPurchase = useSelector(selectIsPurchaseLoanPurposeFromApplication);
  const isAdditionalLoan = useSelector(selectIsAdditionalLoanPurposeFromApplication);
  const isHelocPurposePurchase = useSelector(
    selectIsHelocPurposePurchaseFromPropertyAndLoanSummary,
  );

  const isMobile = useSelector(selectIsMobile);
  const isTablet = useSelector(selectIsTablet);

  const baseConfig =
    isPurchase || (isAdditionalLoan && isHelocPurposePurchase)
      ? additionalMortgagesConfig
      : liabilitiesConfig;

  const visibleColumns = useMemo(() => {
    if (!isMobile && !isTablet) {
      return null;
    }

    const isAdditionalMortgagesTable = isPurchase || (isAdditionalLoan && isHelocPurposePurchase);

    return isAdditionalMortgagesTable
      ? [
          'creditName',
          'amount',
          ...getConditionalArray(isTablet, ['monthlyPayment', 'lienPriority']),
        ]
      : [
          'ownersInfo',
          'unpaidBalance',
          ...getConditionalArray(isTablet, ['monthlyPayment', 'creditorName']),
        ];
  }, [isPurchase, isAdditionalLoan, isHelocPurposePurchase, isMobile, isTablet]);

  const config = useMemo(() => {
    if (!visibleColumns) {
      return baseConfig;
    }

    // Calculate equal width for visible columns (table uses table-layout: fixed)
    const columnWidth = `${COLUMN_WIDTH / visibleColumns.length}%`;

    return Object.fromEntries(
      Object.entries(baseConfig).map(([key, columnConfig]) => {
        const isVisible = visibleColumns.includes(key);

        if (isVisible) {
          return [key, { ...columnConfig, width: columnWidth, isHidden: false }];
        }

        return [key, { ...columnConfig, isHidden: true }];
      }),
    );
  }, [baseConfig, visibleColumns]);

  const dataSource =
    isPurchase || (isAdditionalLoan && isHelocPurposePurchase)
      ? secondaryFinancingAdditionalMortgagesData
      : secondaryFinancingLiabilitiesData;
  const notFoundTableData =
    isPurchase || (isAdditionalLoan && isHelocPurposePurchase)
      ? additionalMortgagesNotFoundTableData
      : liabilitiesNotFoundTableData;
  const formattedData = dataSource.length ? dataSource : notFoundTableData;

  return (
    <PageBlock
      withAction
      withDivider
      isActionDisabled={!(isPurchase || (isAdditionalLoan && isHelocPurposePurchase))}
      isMenuOpened={!!anchorEl}
      title="Additional Financing"
      onMenuClick={onSetAnchorElement}
    >
      <TableCDS
        config={config}
        customStyles={tableStyles}
        data={formattedData}
        isCustomDataDefinition={!dataSource.length}
        isLoading={isLoading}
        pendingChangesTextSelector="span, p"
      />
      <ActionMenu
        anchorEl={anchorEl}
        editText="Edit"
        entityType={ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK}
        section={LOAN_AND_LOCK_SECTION_HREF_MAP.ADDITIONAL_FINANCING}
        onClearAnchorElement={onClearAnchorElement}
      />
    </PageBlock>
  );
};

SecondaryFinancing.propTypes = {
  ...WithLoadingPropTypes,
};

export default SecondaryFinancing;
